import { useTranslation } from 'react-i18next';
import { useSelection } from '../../context/SelectionContext';
import { useChildren } from '../../hooks/useChildren';
import { useConsultationTasks } from '../../hooks/useConsultationTasks';
import ConsultationTaskCard from './ConsultationTaskCard';
import { SkeletonModuleList } from '../common/Skeleton';
import FadeTransition from '../common/FadeTransition';
import EmptyState from '../common/EmptyState';

const PAGE_PADDING = 'px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20';

function Section({ label, dotClass, tasks }) {
  if (tasks.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label}
      </h3>
      {tasks.map((task) => (
        <ConsultationTaskCard key={task.id} task={task} />
      ))}
    </div>
  );
}

export default function ConsultationTaskList() {
  const { t } = useTranslation('consulting');
  const { selectedChild } = useSelection();
  const { children } = useChildren();

  // Only fetch when the selected child actually belongs to this parent.
  const isValidChild = selectedChild && children.some((c) => c.id === selectedChild.id);
  const { tasks, enrolled, loading, error } = useConsultationTasks(
    isValidChild ? selectedChild?.id : null
  );

  if (!selectedChild) {
    return null;
  }

  const activeTasks = tasks.filter((task) => task.status !== 'COMPLETED');
  const completedTasks = tasks.filter((task) => task.status === 'COMPLETED');
  const hasTasks = tasks.length > 0;

  const getStateKey = () => {
    if (loading) return 'loading';
    if (error) return 'error';
    if (!hasTasks) return enrolled ? 'empty' : 'not-enrolled';
    return 'content';
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={PAGE_PADDING}>
          <SkeletonModuleList count={4} />
        </div>
      );
    }

    if (error) {
      return (
        <div className={PAGE_PADDING}>
          <EmptyState illustration="noData" message={t('error')} />
        </div>
      );
    }

    if (!hasTasks) {
      return (
        <div className={PAGE_PADDING}>
          {enrolled ? (
            <EmptyState
              illustration="noModules"
              title={t('empty.noTasksTitle')}
              description={t('empty.noTasksDesc')}
            />
          ) : (
            <EmptyState
              illustration="noData"
              title={t('empty.notEnrolledTitle')}
              description={t('empty.notEnrolledDesc')}
            />
          )}
        </div>
      );
    }

    return (
      <div className={PAGE_PADDING}>
        <Section label={t('sections.active')} dotClass="bg-[#589DD5]" tasks={activeTasks} />
        <Section label={t('sections.completed')} dotClass="bg-green-500" tasks={completedTasks} />
      </div>
    );
  };

  const transitionKey = `${selectedChild?.id}-${getStateKey()}`;

  return (
    <>
      {/* Persistent page header — clarifies this is the essay-consulting program
          (distinct from the appointment "consultation") and stays put across states. */}
      <div className="px-4 pt-4 sm:px-6 sm:pt-5 md:px-12 lg:px-20">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {t('title')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('subtitle')}
        </p>
      </div>
      <FadeTransition transitionKey={transitionKey}>{renderContent()}</FadeTransition>
    </>
  );
}
